import React, { useEffect } from "react";
import CreateStepsLayout from "../CreateStepsLayout";
import { CREATE_PROJECT_STEPS, PROJECT_FLOW_TYPES } from "../../constants";
import { CheckCircleRounded, ChevronRightRounded } from "@mui/icons-material";
import {
  Button,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
  useTheme
} from "@mui/material";
import { useAppDispatch, useTypedSelector } from "@/config/configureAppStore";
import { useNavigate } from "react-router-dom";
import {
  FileUploadStatus,
  ProjectSummary,
  AddProjectName,
  UploadFiles,
  RequirementsLanguageSelector,
  RequirementsServiceSelector,
  UPLOAD_FILES_ENTRY_POINT
} from "./components";
import { resetState } from "@/reducers/slices/clientProjectPopupSlice/clientProjectPopupSlice";
import uppyFactory from "../../uppyFactory";
import {
  setCreateProjectDialog,
  setUploadFilesModal
} from "@/reducers/slices/dialogSlice/dialogSlice";
import useCreateProject from "../../hooks/useCreateProject";
import { CreditsInfo } from "@/features/clientProjectDetails";
import { MurfInputLabel } from "@/components/elements/MurfInputLabel";
import SelectSampleFiles from "./components/SelectSampleFiles";

export const CreateProjectFlow = (): React.ReactNode => {
  const theme = useTheme();
  const currentStep = useTypedSelector(
    (state) => state.clientProjectPopup.currentStep
  );
  const projectId = useTypedSelector(
    (state) => state.clientProjectPopup.projectId
  );
  const uploadedSampleFileIds = useTypedSelector(
    (state) => state.clientProjectPopup.uploadedSampleFileIds
  );

  const { currentFlowType } = useCreateProject();

  const project = useTypedSelector((state) => state.clientProjectPopup);

  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const createStepsLayoutProps = {
    title: Object.values(CREATE_PROJECT_STEPS)[currentStep]?.title
    // caption: Object.values(CREATE_PROJECT_STEPS)[currentStep]?.caption
  };

  useEffect(() => {
    uppyFactory.setEntryPoint("Create Project");
  }, []);

  // @TODO: extract this code to the useCreateProject Hook
  useEffect(() => {
    let el: any = document.querySelector(
      "#create-project-wrapper [tabIndex='0']"
    );
    switch (currentStep) {
      case CREATE_PROJECT_STEPS.UPLOAD_FILES.int:
        el = document.getElementById("file-drag-n-drop-wrapper");
        if (el) {
          el.focus();
        }
        break;

      case CREATE_PROJECT_STEPS.FINISH.int:
        // do nothing
        break;
      default:
        if (el) {
          el.focus();
        }
        break;
    }
  }, [currentStep]);

  if (currentStep == CREATE_PROJECT_STEPS.UPLOAD_FILES.int) {
    return (
      <CreateStepsLayout
        {...createStepsLayoutProps}
        containerProps={{
          sx: {
            height: "100%",
            p: 0
          },
          component: undefined
        }}
      >
        <UploadFiles entryPoint={UPLOAD_FILES_ENTRY_POINT.CREATE_PROJECT} />
        <SelectSampleFiles />
      </CreateStepsLayout>
    );
  }
  if (currentStep == CREATE_PROJECT_STEPS.SELECT_LANGUAGE.int) {
    return (
      <CreateStepsLayout
        {...createStepsLayoutProps}
        containerProps={{
          sx: {
            flexGrow: "1",
            bgcolor: "transparent",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 3,
            p: 0
          },
          component: undefined
        }}
      >
        {/* Add project name */}
        <AddProjectName />

        {/* Service Requirements */}
        {currentFlowType === PROJECT_FLOW_TYPES.ENTERPRISE ? (
          <Stack gap={1} width={"100%"}>
            <MurfInputLabel sx={{ flexShrink: 0 }}>
              Dubbing Type (Select one)
            </MurfInputLabel>
            <RequirementsServiceSelector minimal />
          </Stack>
        ) : null}
        {/* Select Languages */}
        <RequirementsLanguageSelector
          qcEnabled={project?.qualityCheckEnabled}
        />
        {/* Dubbing Credit Info */}
        <CreditsInfo
          projectId={projectId as string}
          isCreatingProject
          title="Dubbing charges per file"
          subtitle={`Final credit usage will be based on the duration of files in the project${
            uploadedSampleFileIds?.length
              ? " (you won’t be charged for sample files)"
              : ""
          }`}
        />
      </CreateStepsLayout>
    );
  }
  if (currentStep == CREATE_PROJECT_STEPS.REQUIREMENTS.int) {
    return (
      <CreateStepsLayout
        {...createStepsLayoutProps}
        containerProps={{
          sx: {
            flexGrow: "unset",
            p: 0
          },
          component: undefined
        }}
      >
        <RequirementsServiceSelector />
      </CreateStepsLayout>
    );
  }
  if (currentStep == CREATE_PROJECT_STEPS.FINISH.int) {
    return (
      <CreateStepsLayout
        {...createStepsLayoutProps}
        containerProps={{
          sx: {
            flexGrow: "unset",
            p: 0
          },
          component: undefined
        }}
      >
        <ProjectSummary />
      </CreateStepsLayout>
    );
  }

  return (
    <Grid
      container
      gap={4}
      py={4}
      alignItems={"center"}
      justifyContent={"center"}
    >
      <Grid item xs={12} sx={{ display: "flex", justifyContent: "center" }}>
        <CheckCircleRounded color="success" sx={{ height: 48, width: 48 }} />
      </Grid>
      <Grid
        item
        xs={12}
        sx={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          alignItems: "center"
        }}
      >
        <Typography variant="overline" pt={2}>
          Congratulations!
        </Typography>
        <Typography variant="h4" pt={2}>
          Project Created
        </Typography>
        <Typography
          variant="body2"
          pt={1}
          textAlign={"center"}
          color="text.secondary"
        >
          Sit back and relax while we create dubs for you! Track all updates in
          the project page.
          <br />
          All updates will also be notified over email
        </Typography>
      </Grid>
      <FileUploadStatus
        fluid={true}
        stackProps={{
          width: "100%",
          px: 5,
          py: 2,
          borderRadius: 4,
          border: `1px solid ${theme.palette.divider}`,
          component: Paper,
          elevation: 0
        }}
      />
      <Grid
        item
        xs={12}
        pt={1}
        sx={{ display: "flex", justifyContent: "center" }}
      >
        <Container
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 2
          }}
        >
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              dispatch(resetState());
              dispatch(setUploadFilesModal({ isListOfFilesOpen: false }));
              dispatch(setCreateProjectDialog(false));
              navigate("/home");
            }}
            size="large"
          >
            Home
          </Button>
          <Button
            variant="contained"
            color="primary"
            endIcon={<ChevronRightRounded />}
            onClick={() => {
              dispatch(resetState());
              dispatch(setCreateProjectDialog(false));
              dispatch(setUploadFilesModal({ isListOfFilesOpen: false }));
              navigate(`/project/${projectId}`);
            }}
            size="large"
          >
            View Project
          </Button>
        </Container>
      </Grid>
    </Grid>
  );
};
